import * as Moment from 'moment/moment';
import { AggDataElement } from '../types/AggDataElement';
import { ChartType } from '../types/ChartType';
import { IntersectionId } from '../types/primitives';
import { timeAsDecimal } from './tstamp';
import { TIME_FORMAT } from './constants';
import { Direction } from '../types/Direction';
import { AggDataResponseItem } from '../types/Response';
import Intersection from '../types/Intersection';

interface FoldedMetric {
  category: string;
  intersectionId: IntersectionId;
  metric: number;
}

type MetricType = 'aog' | 'sf' | 'dsb' | 'traj' | 'totalDelay';

export type ExportDataElement = {
  metric: MetricType,
  intersectionId: IntersectionId,
  direction: Direction,
} & { [key: string]: number };

interface DataDict {
  [key: string]: (AggDataElement & { category: string })[];
}

const sum = (list: number[]): number => list.reduce((sumAcc, curr) => sumAcc + curr);

/**
 * Get the value of the applicable AggDataElement, given a chart type
 * For example: aog -> aogCnt, los -> totalDelay, etc)
 * @param d the AggDataElement
 * @param chartType What chart type should be used to determine the field?
 */
export const getValue = (d: AggDataElement, chartType: ChartType): number => {
  switch (chartType) {
    case 'aog':
      return d.aogCnt;
    case 'sf':
      return d.sfCnt;
    case 'dsb':
      return d.dsbCnt;
    case 'los':
      return d.totalDelay;
    default:
      return 0;
  }
};

/**
 * This function groups data by intersection and category into a dictionary that is indexed by
 * "intersectionId,category" strings
 * @param data The data to fold
 * @param foldOn What to fold the data on. See {@link calculateFoldedMetrics}
 */
export const groupAggDataIntoDict = (data: AggDataElement[],
  foldOn: (element: AggDataElement) => string): DataDict => {
  const dataWithCategory = data.map((d) => ({
    ...d,
    category: foldOn(d),
  }));

  // const intersections: IntersectionId[] = data.reduce((acc: IntersectionId[], curr) => {
  //   acc.push(curr.intersectionId);
  //   return acc;
  // }, []);
  //
  // const starter =

  // This is a dictionary (for quick lookups) indexed by "intersectionId,category"
  return dataWithCategory.reduce((acc: DataDict, curr) => {
    const key = [curr.intersectionId, curr.category].join(',');
    if (acc[key]) {
      acc[key].push(curr);
    } else {
      acc[key] = [curr];
    }
    return acc;
  }, {});
};

/**
 * This folds agg data in on itself by a certain metric, determined by the foldOn function.
 * For example, passing a function that takes an AggDataElement and returns the time
 * of day will fold all the data by time of day.
 *
 * The categories parameter must be the superset of all things possibly returned by foldOn.
 * Sticking with the time of day example, categories would be a list
 * of every time of day, from 00:00 to 23:59.
 *
 * @param data The data to fold
 * @param foldOn The function to use to fold the data
 * @param categories The superset of all possible things returned by foldOn
 * @param chartType The ChartType ('aog', 'sf', ...)
 * @param threshold The threshold trajCnt to still include that folded metric.
 */
export const calculateFoldedMetrics = (data: AggDataElement[],
  foldOn: (element: AggDataElement) => string, categories: string[], chartType: ChartType,
  threshold = 0): FoldedMetric[] => {
  if (data.length > 0) {
    const dataDict = groupAggDataIntoDict(data, foldOn);

    return Object.keys(dataDict)
      .reduce((acc: FoldedMetric[], key) => {
        // Only grab the metric we care about (chartType)
        const metrics = dataDict[key].map((d) => getValue(d, chartType));
        // Sum
        const chartTypeSum = metrics.reduce(((previousValue, currentValue) => (
          previousValue + currentValue)));

        // Only grab the trajCnt
        const trajCnts = dataDict[key].map((d) => d.trajCnt);
        // Sum
        const trajCntSum = trajCnts.reduce(((previousValue, currentValue) => (
          previousValue + currentValue)));

        const [intersectionId, category] = key.split(',');

        if (trajCntSum > threshold) {
          acc.push({
            category,
            intersectionId: parseInt(intersectionId) as IntersectionId,
            metric: chartTypeSum / trajCntSum,
          });
        }
        return acc;
      }, []);
  }
  return [];
};

/**
 * Convert raw agg data returned from the API into JS format compliant AggDataElement objects.
 * This method doesn't change any data, just renames fields.
 * @param rawAggData The raw agg data (traj_cnt, total_delay, etc.)
 */
export const convertToAggData = (rawAggData: AggDataResponseItem[]): AggDataElement[] => (
  rawAggData.map((d) => ({
    timestamp: d.timestamp,
    intersectionId: d.intersection_id,
    movement: d.movement,
    direction: d.direction,
    trajCnt: d.traj_cnt,
    gpsCnt: d.gps_cnt,
    aogCnt: d.aog_cnt,
    sfCnt: d.sf_cnt,
    dsbCnt: d.dsb_cnt,
    totalDelay: d.total_delay,
  }))
);

export const createExportData = (aggData: AggDataElement[]): ExportDataElement[] => {
  if (!aggData.length) return [];

  const foldOn = (d: AggDataElement) => `${Moment.unix(d.timestamp)
    .format(TIME_FORMAT)},${d.direction}`;

  const dataDict = groupAggDataIntoDict(aggData, foldOn);

  interface Total {
    intersectionId: IntersectionId,
    time: number,
    traj: number,
    aog: number,
    sf: number,
    dsb: number,
    totalDelay: number,
    direction: Direction
  }

  const totals: Total[] = Object.keys(dataDict)
    .map((key) => {
      const [intersectionId, time, direction] = key.split(',');

      return {
        intersectionId: parseInt(intersectionId) as IntersectionId,
        direction: direction as Direction,
        time: timeAsDecimal(time),
        traj: sum(dataDict[key].map((d) => d.trajCnt)),
        aog: sum(dataDict[key].map((d) => d.aogCnt)),
        sf: sum(dataDict[key].map((d) => d.sfCnt)),
        dsb: sum(dataDict[key].map((d) => d.dsbCnt)),
        totalDelay: sum(dataDict[key].map((d) => d.totalDelay)),
      };
    });

  totals.sort((a, b) => a.time - b.time);

  const final = totals.reduce((acc: ExportDataElement[], curr) => {
    // These are the basic objects for each intersection/direction/metric
    const newElements = ['aog', 'sf', 'dsb', 'traj', 'totalDelay']
      .map((m) => m as MetricType)
      .map((metricType) => ({
        metric: metricType,
        intersectionId: curr.intersectionId,
        direction: curr.direction,
      } as ExportDataElement));

    // Try grabbing existing elements out of the accumulator
    let elements = acc.filter((value) => (
      value.intersectionId === curr.intersectionId && value.direction === curr.direction));
    // If there wasn't anything in there, use the new ones.
    const wereElementsFound = !!elements.length;
    if (!wereElementsFound) elements = newElements;

    // Pull out each metrics' object
    const aogEl = elements.find((e) => e.metric === 'aog') as ExportDataElement;
    const sfEl = elements.find((e) => e.metric === 'sf') as ExportDataElement;
    const dsbEl = elements.find((e) => e.metric === 'dsb') as ExportDataElement;
    const trajEl = elements.find((e) => e.metric === 'traj') as ExportDataElement;
    const totalDelayEl = elements.find((e) => e.metric === 'totalDelay') as ExportDataElement;

    // Put the time on there
    aogEl[curr.time.toFixed(2)] = curr.aog;
    sfEl[curr.time.toFixed(2)] = curr.sf;
    dsbEl[curr.time.toFixed(2)] = curr.dsb;
    trajEl[curr.time.toFixed(2)] = curr.traj;
    totalDelayEl[curr.time.toFixed(2)] = curr.totalDelay;

    if (!wereElementsFound) {
      acc.push(...elements);
    }
    return acc;
  }, []);
  return final;
};

export const download = (filename: string, text: string): void => {
  const element = document.createElement('a');
  element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`);
  element.setAttribute('download', filename);

  element.style.display = 'none';
  document.body.appendChild(element);

  element.click();

  document.body.removeChild(element);
};

export const getIntersectionName = (
  availableIntersections: Intersection[], intersectionId: IntersectionId,
): string => {
  const intersection = availableIntersections.find((i) => i.intersectionId === intersectionId);
  if (intersection) return intersection.intersectionName;
  return `${intersectionId}`;
};
